import { useList } from "@refinedev/core";
import { Card, Table, Select, Input, Button, Space, Tag, Tooltip, Modal, Form, InputNumber, App, Spin } from "antd";
import {
  SearchOutlined,
  ImportOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/common/PageHeader";
import { useNavigate } from "react-router";
import { useState, useMemo, useCallback, useEffect } from "react";
import type { FarmProductionRecord } from "../../types/farm-production";
import { usePageTitle } from "../../hooks";
import { API_BASE_URL } from "../../utils/constants";

interface FarmOption {
  label: string;
  value: number;
}

interface CountryOption {
  label: string;
  value: number;
}

const SPAIN_ID = 1;

export const FarmProductionList = () => {
  usePageTitle("Producción por Finca");
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [searchText, setSearchText] = useState("");
  const [countryId, setCountryId] = useState<number>(SPAIN_ID);
  const [seasonYear, setSeasonYear] = useState<number | null>(new Date().getFullYear());
  // PR4 — Filtro multi por variedad protegida. Vacío = sin filtro.
  const [varietyFilter, setVarietyFilter] = useState<number[]>([]);
  const [editRecord, setEditRecord] = useState<FarmProductionRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingCitrumelo, setExportingCitrumelo] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [form] = Form.useForm();

  // Farm search state for modal
  const [farmOptions, setFarmOptions] = useState<FarmOption[]>([]);
  const [farmSearchLoading, setFarmSearchLoading] = useState(false);

  // Country options
  const [countries, setCountries] = useState<CountryOption[]>([]);

  // Data from by-country endpoint
  const [allData, setAllData] = useState<FarmProductionRecord[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = localStorage.getItem("access_token") || "";
        const res = await fetch(`${API_BASE_URL}/countries/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setCountries((data as { id: number; name: string }[]).map((c) => ({ value: c.id, label: c.name })));
      } catch { /* ignore */ }
    };
    fetchCountries();
  }, []);

  // Fetch data by country and year
  const fetchData = useCallback(async (cid: number, year: number | null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token") || "";
      let url = `${API_BASE_URL}/farm-production-records/by-country/?country_id=${cid}`;
      if (year) url += `&season_year=${year}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAllData(data.results || []);
      setAvailableYears(data.available_years || []);
    } catch (err) {
      message.error("Error al cargar datos: " + String(err));
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchData(countryId, seasonYear);
  }, [countryId, seasonYear, fetchData]);

  const filteredData = useMemo(() => {
    const varietySet =
      varietyFilter.length > 0 ? new Set(varietyFilter) : null;
    const q = searchText.trim().toLowerCase();
    return allData.filter((r) => {
      // PR4 — Filtro por variedad. Las filas sin `variety_id` (raras
      // tras backfill) sólo pasan si no hay filtro activo.
      if (varietySet) {
        if (r.variety_id == null || !varietySet.has(r.variety_id)) return false;
      }
      if (!q) return true;
      return (
        (r.producer_name || "").toLowerCase().includes(q) ||
        (r.farm_name || "").toLowerCase().includes(q) ||
        (r.province_name || "").toLowerCase().includes(q) ||
        (r.town_name || "").toLowerCase().includes(q) ||
        (r.packers || "").toLowerCase().includes(q) ||
        (r.variety_name || "").toLowerCase().includes(q)
      );
    });
  }, [allData, searchText, varietyFilter]);

  // PR4 — Catálogo de variedades para el filtro multi.
  const { result: varietyResult } = useList<{ id: number; name: string; code: string }>({
    resource: "varieties",
    pagination: { mode: "off" },
    queryOptions: { staleTime: 5 * 60 * 1000 },
  });
  const varietyOptions = (varietyResult?.data ?? []).map((v: any) => ({
    value: v.id,
    label: v.name,
  }));

  const fmtKg = (v: number | null) =>
    v != null ? v.toLocaleString("es-ES", { maximumFractionDigits: 0 }) : "—";

  const fmtHa = (v: number | null) =>
    v != null ? v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";

  const kgPerHa = (produced: number | null, surface: number | null) => {
    if (produced == null || surface == null || surface === 0) return "—";
    return Math.round(produced / surface).toLocaleString("es-ES");
  };

  const diffTag = (audited: number | null, produced: number | null) => {
    if (audited == null || produced == null || audited === 0) return null;
    const pct = ((produced - audited) / audited) * 100;
    const color = pct > 10 ? "green" : pct < -10 ? "red" : "blue";
    return <Tag color={color}>{pct > 0 ? "+" : ""}{pct.toFixed(1)}%</Tag>;
  };

  // --- Farm search for modal ---
  const searchFarms = useCallback(async (query: string) => {
    if (query.length < 2) { setFarmOptions([]); return; }
    setFarmSearchLoading(true);
    try {
      const token = localStorage.getItem("access_token") || "";
      const res = await fetch(
        `${API_BASE_URL}/farms/?search=${encodeURIComponent(query)}&page_size=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const results = json.results || json;
      setFarmOptions(
        (results as { id: number; name: string; producer?: { name?: string }; province?: { name?: string }; town?: { name?: string } }[]).map(
          (f) => ({
            value: f.id,
            label: `${f.producer?.name || ""} - ${f.name} - ${f.province?.name || ""} (${f.town?.name || ""})`,
          })
        )
      );
    } catch {
      setFarmOptions([]);
    } finally {
      setFarmSearchLoading(false);
    }
  }, []);

  const openEdit = (record: FarmProductionRecord) => {
    setEditRecord(record);
    form.setFieldsValue({
      audited_kg: record.audited_kg,
      produced_kg: record.produced_kg,
      farm: record.farm,
      packers: record.packers,
    });
    if (record.farm && record.farm_display_name) {
      setFarmOptions([{ value: record.farm, label: record.farm_display_name }]);
    } else {
      setFarmOptions([]);
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!editRecord) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem("access_token") || "";

      if (editRecord.id) {
        // Update existing record
        const res = await fetch(`${API_BASE_URL}/farm-production-records/${editRecord.id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            audited_kg: values.audited_kg,
            produced_kg: values.produced_kg,
            farm: values.farm ?? null,
            packers: values.packers ?? "",
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        message.success("Registro actualizado");
      } else {
        // Create new record
        const res = await fetch(`${API_BASE_URL}/farm-production-records/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            audited_kg: values.audited_kg,
            produced_kg: values.produced_kg,
            farm: editRecord.farm_id ?? values.farm ?? null,
            season_year: seasonYear ?? new Date().getFullYear(),
            producer_name: editRecord.producer_name || "",
            farm_name: editRecord.farm_name || "",
            province_name: editRecord.province_name || "",
            town_name: editRecord.town_name || "",
            external_farm_id: editRecord.farm_id ?? 0,
            packers: values.packers ?? "",
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        message.success("Registro creado");
      }

      setModalOpen(false);
      setEditRecord(null);
      fetchData(countryId, seasonYear);
    } catch (err) {
      message.error("Error al guardar: " + String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("access_token") || "";
      const res = await fetch(`${API_BASE_URL}/farm-production-records/export-plots-excel/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "produccion_parcelas_espana.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success("Excel descargado correctamente");
    } catch (err) {
      message.error("Error al exportar: " + String(err));
    } finally {
      setExporting(false);
    }
  };

  const handleExportCitrumelo = async () => {
    setExportingCitrumelo(true);
    try {
      const token = localStorage.getItem("access_token") || "";
      const res = await fetch(
        `${API_BASE_URL}/farm-production-records/export-citrumelo-excel/?country_id=${countryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rendimiento_citrumelo.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success("Informe Citrumelo descargado");
    } catch (err) {
      message.error("Error al exportar: " + String(err));
    } finally {
      setExportingCitrumelo(false);
    }
  };

  const handleAutoAssign = () => {
    if (!seasonYear) {
      message.warning("Selecciona un año de campaña primero");
      return;
    }
    modal.confirm({
      title: "¿Auto-asignar producción?",
      content: `Se asignará producción estimada a las fincas sin dato de producción real para la campaña ${seasonYear}. Prioridad: 1) Kg auditados, 2) Estimación del productor, 3) Kg estimados.`,
      okText: "Asignar",
      cancelText: "Cancelar",
      onOk: async () => {
        setAutoAssigning(true);
        try {
          const token = localStorage.getItem("access_token") || "";
          const res = await fetch(`${API_BASE_URL}/farm-production-records/auto-assign-production/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ country_id: countryId, season_year: seasonYear }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const result = await res.json();
          message.success(
            `Asignados: ${result.total_assigned} (${result.assigned_from_audit_kg} por auditoría, ${result.assigned_from_producer_estimation} por est. productor, ${result.assigned_from_estimated_kg} por estimados). Creados: ${result.created_records}. Sin dato: ${result.skipped_no_data}.`
          );
          fetchData(countryId, seasonYear);
        } catch (err) {
          message.error("Error al auto-asignar: " + String(err));
        } finally {
          setAutoAssigning(false);
        }
      },
    });
  };

  return (
    <Card>
      <PageHeader
        icon={<BarChartOutlined />}
        accent="#FA541C"
        title="Producción por Finca"
        subtitle="Producción declarada por finca y campaña"
        extra={
          <Space wrap>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleAutoAssign}
              loading={autoAssigning}
              disabled={!seasonYear}
            >
              Auto-asignar producción
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportCitrumelo} loading={exportingCitrumelo}>
              Informe Citrumelo
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportExcel} loading={exporting}>
              Descargar Excel Parcelas
            </Button>
            <Button type="primary" icon={<ImportOutlined />} onClick={() => navigate("/farm-production/import")}>
              Importar JSON
            </Button>
          </Space>
        }
      />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={countryId}
          style={{ width: 180 }}
          onChange={(val) => setCountryId(val)}
          options={countries}
          placeholder="País"
        />
        <Select
          value={seasonYear}
          style={{ width: 150 }}
          onChange={(val) => setSeasonYear(val ?? null)}
          allowClear
          placeholder="Año campaña"
          options={availableYears.map((y) => ({ label: String(y), value: y }))}
        />
        <Input
          placeholder="Buscar productor, finca, provincia..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
        />
        <Select<number[]>
          mode="multiple"
          options={varietyOptions}
          placeholder="Variedad protegida"
          value={varietyFilter}
          onChange={(value) =>
            setVarietyFilter(Array.isArray(value) ? (value as number[]) : [])
          }
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 240 }}
          maxTagCount="responsive"
        />
      </Space>

      <Spin spinning={loading}>
        <Table
          dataSource={filteredData}
          // PR4 — rowKey incluye la variedad porque una finca puede
          // aparecer en N filas (una por variedad) en la misma campaña.
          rowKey={(r) =>
            `${r.id ?? r.farm_id ?? `${r.producer_name}-${r.farm_name}`}-${r.variety_id ?? "n"}`
          }
          size="small"
          scroll={{ x: 1700 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "200"] }}
          onRow={(record) => ({
            onClick: () => openEdit(record),
            style: { cursor: "pointer" },
          })}
        >
          <Table.Column title="Productor" dataIndex="producer_name" key="producer_name" ellipsis
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.producer_name || "").localeCompare(b.producer_name || "")}
          />
          <Table.Column title="Finca" dataIndex="farm_name" key="farm_name" ellipsis
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.farm_name || "").localeCompare(b.farm_name || "")}
          />
          <Table.Column title="Provincia" dataIndex="province_name" key="province_name" width={120}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.province_name || "").localeCompare(b.province_name || "")}
          />
          <Table.Column title="Municipio" dataIndex="town_name" key="town_name" width={120}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.town_name || "").localeCompare(b.town_name || "")}
          />
          <Table.Column
            title="Variedad protegida"
            dataIndex="variety_name"
            key="variety_name"
            width={160}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) =>
              (a.variety_name || "").localeCompare(b.variety_name || "")
            }
            render={(_: unknown, record: FarmProductionRecord) => {
              if (!record.variety_id) {
                return (
                  <Tooltip title="Sin variedad asignada">
                    <Tag color="warning">Sin asignar</Tag>
                  </Tooltip>
                );
              }
              return (
                <Tag color="blue" style={{ margin: 0 }}>
                  {record.variety_name}
                </Tag>
              );
            }}
          />
          <Table.Column title="Superficie (ha)" dataIndex="farm_surface" key="farm_surface" width={140}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.farm_surface ?? 0) - (b.farm_surface ?? 0)}
            render={fmtHa}
          />
          <Table.Column title="Est. Productor" dataIndex="audit_producer_estimation" key="audit_producer_estimation" width={130}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.audit_producer_estimation ?? 0) - (b.audit_producer_estimation ?? 0)}
            render={fmtKg}
          />
          <Table.Column title="Kg Auditados" dataIndex="audit_kg_plot" key="audit_kg_plot" width={130}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.audit_kg_plot ?? 0) - (b.audit_kg_plot ?? 0)}
            render={fmtKg}
          />
          <Table.Column title="Estimado (kg)" dataIndex="audited_kg" key="audited_kg" width={130}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.audited_kg ?? 0) - (b.audited_kg ?? 0)}
            render={fmtKg}
          />
          <Table.Column title="Producido (kg)" dataIndex="produced_kg" key="produced_kg" width={130}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.produced_kg ?? 0) - (b.produced_kg ?? 0)}
            render={fmtKg}
          />
          <Table.Column title="kg/ha" key="kg_ha" width={90}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => {
              const kgA = (a.produced_kg && a.farm_surface && a.farm_surface > 0) ? a.produced_kg / a.farm_surface : 0;
              const kgB = (b.produced_kg && b.farm_surface && b.farm_surface > 0) ? b.produced_kg / b.farm_surface : 0;
              return kgA - kgB;
            }}
            render={(_: unknown, record: FarmProductionRecord) => kgPerHa(record.produced_kg, record.farm_surface)}
          />
          <Table.Column title="Diferencia" key="diff" width={100}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => {
              const pctA = (a.audited_kg && a.audited_kg !== 0 && a.produced_kg != null) ? ((a.produced_kg - a.audited_kg) / a.audited_kg) : -Infinity;
              const pctB = (b.audited_kg && b.audited_kg !== 0 && b.produced_kg != null) ? ((b.produced_kg - b.audited_kg) / b.audited_kg) : -Infinity;
              return pctA - pctB;
            }}
            render={(_: unknown, record: FarmProductionRecord) => diffTag(record.audited_kg, record.produced_kg)}
          />
          <Table.Column title="Producción" dataIndex="has_production" key="has_production" width={100}
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.has_production ? 1 : 0) - (b.has_production ? 1 : 0)}
            render={(v: boolean) => v ? <Tag color="green">Sí</Tag> : <Tag color="default">No</Tag>}
          />
          <Table.Column title="Etiquetadores" dataIndex="packers" key="packers" ellipsis
            sorter={(a: FarmProductionRecord, b: FarmProductionRecord) => (a.packers || "").localeCompare(b.packers || "")}
          />
        </Table>
      </Spin>

      <Modal
        title={editRecord ? `${editRecord.producer_name} — ${editRecord.farm_name} (${editRecord.season_year})` : "Editar registro"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditRecord(null); }}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Guardar"
        cancelText="Cancelar"
        destroyOnClose
        width={520}
      >
        {editRecord && (
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Estimado (kg)" name="audited_kg">
              <InputNumber style={{ width: "100%" }} min={0} precision={0}
                formatter={(v) => v ? Number(v).toLocaleString("es-ES") : ""}
                parser={(v) => Number((v || "").replace(/\./g, "").replace(",", ".")) as 0}
              />
            </Form.Item>
            <Form.Item label="Producido (kg)" name="produced_kg">
              <InputNumber style={{ width: "100%" }} min={0} precision={0}
                formatter={(v) => v ? Number(v).toLocaleString("es-ES") : ""}
                parser={(v) => Number((v || "").replace(/\./g, "").replace(",", ".")) as 0}
              />
            </Form.Item>
            <Form.Item label="Finca vinculada" name="farm">
              <Select
                showSearch
                allowClear
                placeholder="Buscar finca por nombre o productor..."
                filterOption={false}
                onSearch={searchFarms}
                loading={farmSearchLoading}
                options={farmOptions}
                notFoundContent={farmSearchLoading ? "Buscando..." : "Escribe para buscar"}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              />
            </Form.Item>
            <Form.Item label="Etiquetadores" name="packers">
              <Input placeholder="Etiquetadores separados por coma" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
};
